import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../../components/Breadcrumbs";

// Self-contained, modern Clients LIST view — the design-language sibling of
// the card grid (Clients.jsx). Scoped under `.bpcl` so it neither depends on
// the global theme nor leaks. Dense, sortable rows with gradient avatars and
// status pills; the whole row links to the profile (no navigate()).

const FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function hueFromString(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  return h;
}
const initials = (name) => {
  const n = (name || "").trim().split(/\s+/);
  return n.length > 1 ? `${n[0][0]}${n[1][0]}`.toUpperCase() : (n[0]?.[0] || "N/A").toUpperCase();
};
const asText = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v || "");

const ClientList = () => {
  const clients = useSelector((state) => state.clients.clients);
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "clientName", dir: 1 });

  const rows = useMemo(() => {
    const out = (clients || []).filter((c) => {
      const okStatus = filter === "all" || c?.status?.toLowerCase() === filter;
      const okSearch = c?.clientName?.toLowerCase().includes(search.toLowerCase());
      return okStatus && okSearch;
    });
    const { key, dir } = sort;
    return out.sort((a, b) => {
      const av = asText(a?.[key]).toLowerCase();
      const bv = asText(b?.[key]).toLowerCase();
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [clients, filter, search, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const sortIcon = (key) => (sort.key !== key ? "" : sort.dir === 1 ? " ▲" : " ▼");

  const COLS = [
    { key: "clientName", label: "Client", sortable: true },
    { key: "department", label: "Department", sortable: true },
    { key: "ServicesAssigned", label: "Services Assigned", sortable: false },
    { key: "BlueplanitReportingLead", label: "Reporting Lead", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="Clients"
            title="Dashboard"
            subtitle="Clients"
            modal="#add_client"
            name="Add Client"
            Linkname="/clients"
            Linkname1="/clients-list"
            view="list"
          />

          <div className="bpcl">
            <style>{BPCL_LIST_STYLES}</style>

            {/* Controls — identical to the grid for consistency */}
            <div className="bpcl-controls">
              <div className="bpcl-segmented" role="tablist" aria-label="Status filter">
                {FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    role="tab"
                    aria-selected={filter === f.value}
                    className={`bpcl-chip ${filter === f.value ? "is-active" : ""}`}
                    onClick={() => setFilter(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="bpcl-search">
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                  <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <path d="m20 20-3.2-3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <input placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} />
                {search ? (
                  <button type="button" className="bpcl-clear" aria-label="Clear" onClick={() => setSearch("")}>×</button>
                ) : null}
              </div>
              <span className="bpcl-count">{rows.length} clients</span>
            </div>

            {/* List */}
            {rows.length === 0 ? (
              <div className="bpcl-empty">
                <div className="bpcl-empty-icon">🔍</div>
                <div className="bpcl-empty-title">No clients found</div>
                <div className="bpcl-empty-body">
                  {search ? "Try a different name or clear the search." : "No clients match this filter."}
                </div>
              </div>
            ) : (
              <div className="bpcl-table-wrap">
                <table className="bpcl-table">
                  <thead>
                    <tr>
                      {COLS.map((c) => (
                        <th
                          key={c.key}
                          className={c.sortable ? "is-sortable" : ""}
                          onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                        >
                          {c.label}
                          <span className="bpcl-sort">{sortIcon(c.key)}</span>
                        </th>
                      ))}
                      <th aria-label="Open" style={{ width: 44 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => {
                      const isActive = c?.status?.toLowerCase() === "active";
                      const hue = hueFromString(c?.clientName || "");
                      return (
                        <tr key={c?.id} className="bpcl-row" onClick={() => {}}>
                          <td>
                            <Link to={`/clients/${c?.id}`} className="bpcl-client-cell">
                              <span
                                className="bpcl-avatar-sm"
                                style={{ background: `linear-gradient(135deg, hsl(${hue} 72% 56%), hsl(${(hue + 38) % 360} 70% 44%))` }}
                              >
                                {initials(c?.clientName)}
                              </span>
                              <span className="bpcl-client-name" title={c?.clientName}>
                                {c?.clientName || "Unnamed client"}
                              </span>
                            </Link>
                          </td>
                          <td>{c?.department ? <span className="bpcl-tag">{c.department}</span> : <span className="bpcl-dash">—</span>}</td>
                          <td className="bpcl-trunc" title={asText(c?.ServicesAssigned)}>
                            {asText(c?.ServicesAssigned) || <span className="bpcl-dash">—</span>}
                          </td>
                          <td className="bpcl-trunc" title={asText(c?.BlueplanitReportingLead)}>
                            {asText(c?.BlueplanitReportingLead) || <span className="bpcl-dash">—</span>}
                          </td>
                          <td>
                            <span className={`bpcl-status-pill ${isActive ? "on" : "off"}`}>
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <Link to={`/clients/${c?.id}`} className="bpcl-open" aria-label="Open profile">→</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const BPCL_LIST_STYLES = `
.bpcl { --bpcl-ink:#0f172a; --bpcl-muted:#64748b; --bpcl-line:#e8ecf2; --bpcl-accent:#2563eb; color:var(--bpcl-ink); }
.bpcl-controls { display:flex; align-items:center; gap:12px; margin:4px 0 18px; flex-wrap:wrap; }
.bpcl-segmented { display:inline-flex; gap:2px; padding:3px; background:#f1f4f9; border:1px solid var(--bpcl-line); border-radius:999px; }
.bpcl-chip { border:0; background:transparent; color:var(--bpcl-muted); font-size:13px; font-weight:600; padding:6px 14px; border-radius:999px; cursor:pointer; transition:all .15s ease; }
.bpcl-chip:hover { color:var(--bpcl-ink); }
.bpcl-chip.is-active { background:#fff; color:var(--bpcl-accent); box-shadow:0 1px 2px rgba(16,24,40,.08); }
.bpcl-search { display:inline-flex; align-items:center; gap:8px; background:#fff; border:1px solid var(--bpcl-line); border-radius:10px; padding:0 12px; height:38px; min-width:240px; color:var(--bpcl-muted); transition:border-color .15s, box-shadow .15s; }
.bpcl-search:focus-within { border-color:var(--bpcl-accent); box-shadow:0 0 0 3px rgba(37,99,235,.14); }
.bpcl-search input { border:0; outline:0; background:transparent; font-size:14px; color:var(--bpcl-ink); width:100%; }
.bpcl-clear { border:0; background:transparent; color:var(--bpcl-muted); font-size:18px; line-height:1; cursor:pointer; padding:0 2px; }
.bpcl-count { margin-left:auto; font-size:13px; color:var(--bpcl-muted); font-weight:500; }

.bpcl-table-wrap { background:#fff; border:1px solid var(--bpcl-line); border-radius:14px; overflow:hidden; box-shadow:0 1px 2px rgba(16,24,40,.04); }
.bpcl-table { width:100%; border-collapse:collapse; font-size:13.5px; }
.bpcl-table thead th { text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--bpcl-muted); padding:12px 16px; background:#f8fafc; border-bottom:1px solid var(--bpcl-line); white-space:nowrap; user-select:none; }
.bpcl-table thead th.is-sortable { cursor:pointer; }
.bpcl-table thead th.is-sortable:hover { color:var(--bpcl-ink); }
.bpcl-sort { color:var(--bpcl-accent); font-size:10px; }
.bpcl-table tbody td { padding:11px 16px; border-bottom:1px solid #f1f4f9; vertical-align:middle; max-width:0; }
.bpcl-table tbody tr:last-child td { border-bottom:0; }
.bpcl-row { transition:background .12s ease; }
.bpcl-row:hover { background:#f6f9ff; }

.bpcl-client-cell { display:inline-flex; align-items:center; gap:10px; text-decoration:none; color:var(--bpcl-ink); max-width:100%; }
.bpcl-avatar-sm { flex-shrink:0; width:34px; height:34px; border-radius:10px; display:inline-flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#fff; box-shadow:0 3px 8px -3px rgba(16,24,40,.3); }
.bpcl-client-name { font-weight:650; letter-spacing:-.01em; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.bpcl-client-cell:hover .bpcl-client-name { color:var(--bpcl-accent); }

.bpcl-trunc { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:#475569; }
.bpcl-dash { color:#cbd5e1; }
.bpcl-tag { font-size:11px; font-weight:600; color:#475569; background:#f1f4f9; border:1px solid var(--bpcl-line); padding:2px 9px; border-radius:999px; white-space:nowrap; }
.bpcl-status-pill { font-size:11px; font-weight:700; padding:3px 10px; border-radius:999px; display:inline-flex; align-items:center; gap:5px; }
.bpcl-status-pill::before { content:""; width:6px; height:6px; border-radius:50%; }
.bpcl-status-pill.on { color:#15803d; background:#dcfce7; } .bpcl-status-pill.on::before { background:#16a34a; }
.bpcl-status-pill.off { color:#b91c1c; background:#fee2e2; } .bpcl-status-pill.off::before { background:#dc2626; }
.bpcl-open { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:8px; color:#94a3b8; text-decoration:none; font-size:15px; transition:all .15s ease; }
.bpcl-row:hover .bpcl-open { color:var(--bpcl-accent); background:#eaf1ff; transform:translateX(2px); }

.bpcl-empty { text-align:center; padding:64px 20px; color:var(--bpcl-muted); background:#fff; border:1px dashed #d3dbe7; border-radius:16px; }
.bpcl-empty-icon { font-size:26px; margin-bottom:8px; }
.bpcl-empty-title { font-size:16px; font-weight:700; color:var(--bpcl-ink); }
.bpcl-empty-body { font-size:13px; margin-top:4px; }
`;

export default ClientList;
